import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useModalScrollLock } from '../../hooks/useModalScrollLock'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: React.ReactNode
  children: React.ReactNode
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | 'full'
  showCloseButton?: boolean
  className?: string
  overlayClassName?: string
  containerClassName?: string
  closeOnEscape?: boolean
  closeOnOutsideClick?: boolean
  ariaLabel?: string
}

const MAX_WIDTH_CLASSES: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  full: 'max-w-full m-2',
}

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = '2xl',
  showCloseButton = true,
  className = '',
  overlayClassName = '',
  containerClassName = '',
  closeOnEscape = true,
  closeOnOutsideClick = true,
  ariaLabel,
}: ModalProps) {
  // Lock document body scrolling when modal is open
  useModalScrollLock(isOpen)

  // Escape key handler
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, closeOnEscape, onClose])

  if (!isOpen) return null

  const modalContent = (
    <div
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : ariaLabel}
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto bg-black/60 backdrop-blur-xs transition-opacity animate-fadeIn ${overlayClassName}`}
      onClick={(e) => {
        if (closeOnOutsideClick && e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div
        className={`bg-white rounded-2xl border-2 border-[#F6EEE7] w-full shadow-2xl overflow-hidden my-auto max-h-[90vh] flex flex-col ${MAX_WIDTH_CLASSES[maxWidth] || 'max-w-2xl'} ${containerClassName} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-[#F6EEE7] shrink-0 bg-white">
            {title && (
              <div className="text-xl sm:text-2xl font-bold text-[#6D3A0D]">
                {title}
              </div>
            )}
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition focus:outline-none ml-auto"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        {/* Modal Body */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 text-gray-800">
          {children}
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
